// packages/web/src/components/teach/ExerciseUploadSection.tsx
import React, { useState } from 'react';
import { useTeacherLearningStore } from '../../stores/teacherLearningStore';
import { teacherLearningApi } from '../../services/teacherLearningApi';
import { ImageCropModal } from '../chat/ImageCropModal';
import { apiService } from '@shared/services/api/ApiService';
import { useStore } from '../../stores';
import { buildStaticUrl } from '../../config/env';
import { MembershipWarningModal } from './MembershipWarningModal';

export const ExerciseUploadSection: React.FC = () => {
  const {
    exerciseImagePreview,
    step1Status,
    setExercises,
    setStepStatus,
    setErrorMessage,
    setCurrentStep,
    setExerciseImage,
  } = useTeacherLearningStore();

  const { user } = useStore();
  const [showCropModal, setShowCropModal] = useState(false);
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
  const [pendingImageUrl, setPendingImageUrl] = useState<string | null>(null);
  const [membershipModal, setMembershipModal] = useState<{
    isOpen: boolean;
    message: string;
    type: 'warning' | 'error';
    qrCodeUrl?: string;
    alreadyBound?: boolean;
  }>({ isOpen: false, message: '', type: 'warning' });

  // 处理图片选择
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setPendingImageFile(file);
    setPendingImageUrl(URL.createObjectURL(file));
    setShowCropModal(true);
  };

  // 处理裁剪确认
  const handleCropConfirm = async (croppedFile: File) => {
    setShowCropModal(false);
    
    // 立即创建本地预览URL（像步骤2一样）
    const localPreviewUrl = URL.createObjectURL(croppedFile);
    setExerciseImage('', localPreviewUrl); // 先显示本地预览
    
    setStepStatus(1, 'loading');

    try {
      // 设置 token
      if (user?.token) {
        apiService.setToken(user.token);
      }

      // 1. 上传裁剪后的图片
      const result = await apiService.uploadFile('/upload/chat-image', croppedFile);

      if (!result.success || !result.data) {
        throw new Error(result.error?.message || '图片上传失败');
      }

      const imagePath = result.data.url;

      if (!imagePath) {
        throw new Error('未获取到图片路径');
      }

      // 2. 保存服务器图片路径到 store（保持本地预览URL）
      setExerciseImage(imagePath, localPreviewUrl);

      // 3. 调用识别 API
      const response = await teacherLearningApi.recognizeExercises({ imagePath });

      if (response.success && response.data) {
        // 显示会员续费提醒（如果有）
        if (response.warning?.message) {
          setMembershipModal({
            isOpen: true,
            message: response.warning.message,
            type: 'warning'
          });
        }

        setStepStatus(1, 'success');
        // Parent users skip step 2 (material upload / generate requirements)
        if (user?.role === 'parent') {
          const exercisesWithOriginal = response.data.exercises.map((ex: any) => ({
            ...ex,
            originalTeacherRequirement: ex.teacher_requirement || '',
          }));
          setExercises(exercisesWithOriginal);
          setStepStatus(2, 'skipped');
          setCurrentStep(3);
        } else {
          setExercises(response.data.exercises);
          setCurrentStep(2);
        }
      } else {
        throw new Error(response.error?.message || '识别失败');
      }
    } catch (error: any) {
      // 会员过期被阻止
      if (error.response?.status === 403 && error.response?.data?.error?.code === 'MEMBERSHIP_REQUIRED') {
        setMembershipModal({
          isOpen: true,
          message: error.response.data.error.message,
          type: 'error',
          qrCodeUrl: error.response.data.error?.qrCodeUrl || error.response.data?.qrCodeUrl,
          alreadyBound: error.response.data.error?.alreadyBound || error.response.data?.alreadyBound
        });
      }
      setStepStatus(1, 'error');
      setErrorMessage(error.response?.data?.error?.message || error.message || '识别习题失败');
      // 失败时清理预览
      if (localPreviewUrl) {
        URL.revokeObjectURL(localPreviewUrl);
      }
      setExerciseImage(null, null);
    } finally {
      // 清理临时文件
      if (pendingImageUrl) {
        URL.revokeObjectURL(pendingImageUrl);
      }
      setPendingImageFile(null);
      setPendingImageUrl(null);
    }
  };

  // 处理裁剪取消
  const handleCropCancel = () => {
    setShowCropModal(false);
    if (pendingImageUrl) {
      URL.revokeObjectURL(pendingImageUrl);
    }
    setPendingImageFile(null);
    setPendingImageUrl(null);
  };

  // 处理删除图片
  const handleDeleteImage = () => {
    // 清理本地预览URL
    if (exerciseImagePreview && exerciseImagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(exerciseImagePreview);
    }
    setExerciseImage(null, null);
    setStepStatus(1, 'idle');
    setExercises([]);
    setCurrentStep(1);
  };

  return (
    <div className="exercise-upload-section bg-white rounded-lg shadow p-6 h-full flex flex-col">
      {/* 头部：标题 + 选择文件按钮 + 状态提示 */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">步骤1：上传习题集图片</h2>
        
        <div className="flex items-center gap-3">
          {/* 状态提示 */}
          {step1Status === 'loading' && (
            <span className="text-sm text-blue-600">正在识别...</span>
          )}
          {step1Status === 'success' && (
            <span className="text-sm text-green-600">✓ 识别成功</span>
          )}
          {step1Status === 'error' && (
            <span className="text-sm text-red-600">✗ 识别失败</span>
          )}
          
          {/* 选择文件按钮 */}
          {!exerciseImagePreview && (
            <label className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 cursor-pointer transition-colors">
              选择文件
              <input
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                disabled={step1Status === 'loading'}
                className="hidden"
              />
            </label>
          )}
        </div>
      </div>

      {/* 预览区域 */}
      <div className="flex-1 min-h-0">
        {!exerciseImagePreview ? (
          <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-8 text-center h-full flex flex-col items-center justify-center">
            <div className="text-4xl mb-4">📸</div>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              选择习题集图片文件
            </p>
            <p className="text-xs text-gray-500">
              支持 JPG、PNG 格式，选择后可裁剪调整
            </p>
          </div>
        ) : (
          <div className="relative h-full">
            <img
              src={exerciseImagePreview}
              alt="习题集预览"
              className="w-full h-full object-contain rounded-xl"
            />
            {step1Status !== 'loading' && (
              <button
                onClick={handleDeleteImage}
                className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm hover:bg-red-600 transition-colors shadow-lg"
                title="删除图片"
              >
                ✕
              </button>
            )}
          </div>
        )}
      </div>

      {/* 图片裁剪模态窗口 */}
      {showCropModal && pendingImageFile && pendingImageUrl && (
        <ImageCropModal
          imageFile={pendingImageFile}
          imageUrl={pendingImageUrl}
          onConfirm={handleCropConfirm}
          onCancel={handleCropCancel}
        />
      )}

      {/* 会员状态提醒模态窗口 */}
      <MembershipWarningModal
        isOpen={membershipModal.isOpen}
        message={membershipModal.message}
        type={membershipModal.type}
        onClose={() => setMembershipModal({ ...membershipModal, isOpen: false })}
        qrCodeUrl={membershipModal.qrCodeUrl}
        alreadyBound={membershipModal.alreadyBound}
      />
    </div>
  );
};
